import {
  addIndex,
  chain,
  concat,
  converge,
  curry,
  defaultTo,
  find,
  head,
  includes,
  isEmpty,
  isNil,
  lensProp,
  map,
  mergeLeft,
  pipe,
  prop,
  propEq,
  reject,
  set,
  uniqBy,
  unless,
  view,
} from "ramda";
import * as steps from "common/steps";
import * as applicationActions from "application/applicationActions";
import * as submissionActions from "./submissionActions";
import { isBlank } from "../common/general";

const initialState = {
  loading: true,
  activeStep: steps.INTRO,
  workTypeId: "",
  workTypes: [],
  cancelSubmissionDialogVisible: false,
  invalidSubmissionDialogVisible: false,
  submissionErrorDialogVisible: false,
  validationErrorsDialogVisible: false,
  uploadDialogVisible: false,
  uploadProgress: 0,
  uploadComplete: false,
  uploadStep: "File",
  submissionFiles: [],
  existingSubmissionsDialogVisible: false,
  existingSubmissionsEmail: "",
  existingSubmissionsChecking: false,
};

const loadingLens = lensProp("loading");
const setLoading = set(loadingLens);
export const getLoading = view(loadingLens);

const activeStepLens = lensProp("activeStep");
const setActiveStep = set(activeStepLens);
export const getActiveStep = view(activeStepLens);
export const isActiveStep = curry(
  (step, state) => getActiveStep(state) === step
);

const workTypeIdLens = lensProp("workTypeId");
const setWorkTypeId = set(workTypeIdLens);
export const getWorkTypeId = view(workTypeIdLens);

const workTypesLens = lensProp("workTypes");
const setWorkTypes = set(workTypesLens);
export const getWorkTypes = view(workTypesLens);

export const getWorkType = pipe(
  converge(
    (id, workTypes) => find(propEq("id", id), workTypes),
    [getWorkTypeId, getWorkTypes]
  ),
  defaultTo({})
);

const submissionFileLens = lensProp("submissionFile");
const setSubmissionFile = set(submissionFileLens);
export const getSubmissionFile = view(submissionFileLens);

const submissionFilesLens = lensProp("submissionFiles");
const setSubmissionFiles = set(submissionFilesLens);
export const getSubmissionFiles = view(submissionFilesLens);

const mapIndexed = addIndex(map);
const getUpdatedSubmissionFiles = (submissionFiles, fileResources) =>
  pipe(
    concat(submissionFiles),
    uniqBy(prop("originalFilename")),
    mapIndexed((file, idx) =>
      mergeLeft(
        {
          order: idx,
          title: isBlank(file.title) ? file.originalFilename : file.title,
        },
        file
      )
    )
  )(fileResources);

const updateSubmissionFiles = (newFiles, state) => {
  const fileType = prop("fileType", head(newFiles));
  const isMultiFileWorkType = includes(state.workTypeId, ["ALBUM"]);

  // Replace files based on fileType -> ["SUBMISSION", "COVER"]
  const submissionFiles = isMultiFileWorkType
    ? getSubmissionFiles(state)
    : reject(propEq("fileType", fileType), getSubmissionFiles(state));

  const updatedSubmissionFiles = getUpdatedSubmissionFiles(
    submissionFiles,
    newFiles
  );

  return setSubmissionFiles(updatedSubmissionFiles, state);
};

const cancelSubmissionDialogVisibleLens = lensProp(
  "cancelSubmissionDialogVisible"
);
const setCancelSubmissionDialogVisible = set(cancelSubmissionDialogVisibleLens);
export const getCancelSubmissionDialogVisible = view(
  cancelSubmissionDialogVisibleLens
);

const invalidSubmissionDialogVisibleLens = lensProp(
  "invalidSubmissionDialogVisible"
);
const setInvalidSubmissionDialogVisible = set(
  invalidSubmissionDialogVisibleLens
);
export const getInvalidSubmissionDialogVisible = view(
  invalidSubmissionDialogVisibleLens
);

const validationErrorsDialogVisibleLens = lensProp(
  "validationErrorsDialogVisible"
);
const setValidationErrorsDialogVisible = set(validationErrorsDialogVisibleLens);
export const getValidationErrorsDialogVisible = view(
  validationErrorsDialogVisibleLens
);

const submissionErrorDialogVisibleLens = lensProp(
  "submissionErrorDialogVisible"
);
const setSubmissionErrorDialogVisible = set(submissionErrorDialogVisibleLens);
export const getSubmissionErrorDialogVisible = view(
  submissionErrorDialogVisibleLens
);

const uploadDialogVisibleLens = lensProp("uploadDialogVisible");
const setUploadDialogVisible = set(uploadDialogVisibleLens);
export const getUploadDialogVisible = view(uploadDialogVisibleLens);

const uploadCompleteLens = lensProp("uploadComplete");
const setUploadComplete = set(uploadCompleteLens);
export const getUploadComplete = view(uploadCompleteLens);

const uploadProgressLens = lensProp("uploadProgress");
const setUploadProgress = set(uploadProgressLens);
export const getUploadProgress = view(uploadProgressLens);

const uploadStepLens = lensProp("uploadStep");
const setUploadStep = set(uploadStepLens);
export const getUploadStep = view(uploadStepLens);

const existingSubmissionsDialogVisibleLens = lensProp(
  "existingSubmissionsDialogVisible"
);
const setExistingSubmissionsDialogVisible = set(
  existingSubmissionsDialogVisibleLens
);
export const getExistingSubmissionsDialogVisible = view(
  existingSubmissionsDialogVisibleLens
);

const existingSubmissionsEmailLens = lensProp("existingSubmissionsEmail");
const setExistingSubmissionsEmail = set(existingSubmissionsEmailLens);
export const getExistingSubmissionsEmail = view(existingSubmissionsEmailLens);

const existingSubmissionsCheckingLens = lensProp("existingSubmissionsChecking");
const setExistingSubmissionsChecking = set(existingSubmissionsCheckingLens);
export const getExistingSubmissionsChecking = view(
  existingSubmissionsCheckingLens
);

const cancelUpload = pipe(
  setUploadDialogVisible(false),
  setUploadComplete(false),
  setUploadProgress(0)
);

const cancelSubmission = pipe(
  setUploadDialogVisible(false),
  setCancelSubmissionDialogVisible(false),
  setActiveStep(initialState.activeStep),
  setSubmissionFiles([]),
  state => {
    const workTypes = getWorkTypes(state);
    const workTypeId =
      workTypes.length === 1 ? workTypes[0].id : initialState.workTypeId;
    return setWorkTypeId(workTypeId, state);
  },
  cancelUpload
);

const invalidSubmission = (firstInvalidStep, state) =>
  pipe(
    setActiveStep(firstInvalidStep),
    setInvalidSubmissionDialogVisible(true)
  )(state);

const localStorageLoaded = (data, state) =>
  pipe(
    setLoading(false),
    unless(
      () => isEmpty(data),
      pipe(
        setActiveStep(data.activeStep),
        setSubmissionFiles(data.submissionFiles),
        state => {
          const workTypes = getWorkTypes(state);
          const workTypeId =
            isEmpty(data.workTypeId) || workTypes.length === 1
              ? workTypes[0].id
              : data.workTypeId;
          return setWorkTypeId(workTypeId, state);
        }
      )
    )
  )(state);

const sendSubmission = setUploadDialogVisible(true);

const submissionError = pipe(
  setSubmissionErrorDialogVisible(true),
  cancelUpload
);

const uploadComplete = pipe(setUploadComplete(true), setUploadProgress(0));

const uploadProgress = (step, progress, state) =>
  pipe(setUploadProgress(progress), setUploadStep(step))(state);

const allWhenEmpty = curry((col, def) =>
  isNil(col) || isEmpty(col) ? def : col
);

const makeContentTypeWorkType = curry(submissionType => ({
  id: submissionType.name,
  display: submissionType.displayName,
  subTypeName: "",
  submissionType: submissionType.name,
  contentTypeName: submissionType.contentType,
  contentTypeDisplay: submissionType.displayName,
  uploadFileAccepts: submissionType.accepts,
  uploadFileAcceptsDisplay: submissionType.displayAccepts,
  contributorRoles: submissionType.contributorRoles,
}));

const makeSubTypeWorkType = curry((submissionType, subType) => ({
  id: `${submissionType.name}-${subType.name}`,
  display: subType.display,
  subTypeName: subType.name,
  submissionType: submissionType.name,
  contentTypeName: submissionType.contentType,
  contentTypeDisplay: submissionType.displayName,
  uploadFileAccepts: submissionType.accepts,
  uploadFileAcceptsDisplay: submissionType.displayAccepts,
  contributorRoles: submissionType.contributorRoles,
}));

export const createWorkTypes = (
  submissionTypes,
  allAudioTypes,
  allDocumentMediumFormats,
  allImageMediumFormats
) =>
  pipe(
    chain(submissionType => {
      if (submissionType.contentType === "AUDIO") {
        return map(
          makeSubTypeWorkType(submissionType),
          allWhenEmpty(submissionType.audioTypes, allAudioTypes)
        );
      } else if (submissionType.contentType === "OTHER_DOCUMENT") {
        return map(
          makeSubTypeWorkType(submissionType),
          allWhenEmpty(submissionType.mediumFormats, allDocumentMediumFormats)
        );
      } else if (submissionType.contentType === "IMAGE") {
        return map(
          makeSubTypeWorkType(submissionType),
          allWhenEmpty(submissionType.mediumFormats, allImageMediumFormats)
        );
      } else {
        return makeContentTypeWorkType(submissionType);
      }
    })
  )(submissionTypes);

const appDataLoaded = (
  { project, audioTypes, documentMediumFormats, imageMediumFormats },
  state
) => {
  const workTypes = createWorkTypes(
    project.submissionTypes,
    audioTypes,
    documentMediumFormats,
    imageMediumFormats
  );

  const workTypeId =
    workTypes.length === 1 ? workTypes[0].id : initialState.workTypeId;

  return pipe(setWorkTypes(workTypes), setWorkTypeId(workTypeId))(state);
};

const submissionReducer = (state = initialState, action, applicationState) => {
  switch (action.type) {
    case submissionActions.ADD_ANOTHER_WORK:
    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case submissionActions.CANCEL_UPLOAD:
      return cancelUpload(state);

    case submissionActions.INVALID_SUBMISSION:
      return invalidSubmission(action.firstInvalidStep, state);

    case submissionActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case submissionActions.SEND_SUBMISSION:
      return sendSubmission(state);

    case submissionActions.UPLOAD_URL_CREATED:
      return setSubmissionFile(action.fileResource, state);

    case submissionActions.SET_ACTIVE_STEP:
      return setActiveStep(action.step, state);

    case submissionActions.SET_WORK_TYPE_ID:
      return setWorkTypeId(action.workTypeId, state);

    case submissionActions.SHOW_CANCEL_SUBMISSION_DIALOG:
      return setCancelSubmissionDialogVisible(action.show, state);

    case submissionActions.SHOW_INVALID_SUBMISSION_DIALOG:
      return setInvalidSubmissionDialogVisible(action.show, state);

    case submissionActions.SHOW_SUBMISSION_ERROR_DIALOG:
      return setSubmissionErrorDialogVisible(action.show, state);

    case submissionActions.SET_EXISTING_SUBMISSIONS_CHECKING:
      return setExistingSubmissionsChecking(action.checking, state);

    case submissionActions.SHOW_EXISTING_SUBMISSIONS_DIALOG:
      return pipe(
        setExistingSubmissionsDialogVisible(action.show),
        setExistingSubmissionsEmail(action.email || ""),
      )(state);

    case submissionActions.SHOW_VALIDATION_ERRORS_DIALOG:
      return setValidationErrorsDialogVisible(action.show, state);

    case submissionActions.SUBMISSION_ERROR:
      return submissionError(state);

    case submissionActions.UPLOAD_COMPLETE:
      return uploadComplete(state);

    case submissionActions.UPLOAD_PROGRESS:
      return uploadProgress(action.step, action.percent, state);

    case submissionActions.SET_SUBMISSION_FILES:
      return setSubmissionFiles(action.fileResources, state);

    case submissionActions.UPDATE_SUBMISSION_FILES:
      return updateSubmissionFiles(action.fileResources, state);

    case applicationActions.APP_DATA_LOADED:
      return appDataLoaded(action.data, state);

    default:
      return state;
  }
};

export default submissionReducer;
